import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges, inject, signal } from '@angular/core';

import { BetBuilderFixture, SuspensionState } from '@cds/betting-offer/domain-specific/bet-builder';
import { PopularBetsConfig, ReturnsMessageConfig, SitecorePopularBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { CurrencyPipe } from '@frontend/sports/common/core/feature/money';
import { GridEvent } from '@frontend/sports/grid/core/feature/model';
import { ModalDialogService } from '@frontend/sports/modal/feature';
import { NativePrice, NativePriceFormatterPipe } from '@frontend/sports/odds/feature/native-price';
import { TrackingSource } from '@frontend/sports/tracking/feature';
import { PopularBetsPodTypes } from '@frontend/sports/types/components/popular-bets';
import { BettingSettingsService, IUserBettingSettings, UserService } from '@frontend/sports/user/feature';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { RxStrategyProvider } from '@rx-angular/cdk/render-strategies';
import { RxUnpatch } from '@rx-angular/template/unpatch';
import { IPickTracking } from 'packages/sports/common/betslip/core/picks/pick-models';
import { Observable, Subject, Subscription, exhaustMap, first, map, merge, partition, tap } from 'rxjs';

import { BetBuilderMarket } from '../../betbuilder/model/bet-builder.model';
import { BetBuilderPickIdType } from '../../common/betslip/betslip-betbuilder-services/bet-builder-betslip-service.model';
import { BetslipConnectorService } from '../../common/betslip/betslip-connector.service';
import { ClearBetslipComponent } from '../../common/betslip/clear-betslip/clear-betslip.component';
import { Selection } from '../../common/betslip/model';
import { SummaryCalculatorService } from '../../common/betslip/summary-calculator.service';
import { PickSourceProvider } from '../../option-pick/pick-source.provider';
import { ModularPickSourceService } from '../../widget/core/modular-pick-source.service';
import { AddToBetslipState, BetBuilderOptionType, ButtonProperties, IPreCreatedOption } from '../popular-bets.models';

@Component({
    imports: [NgClass, CurrencyPipe, NgTemplateOutlet, RxUnpatch, IconCustomComponent, AsyncPipe],
    selector: 'ms-popular-bets-pod-add-to-betslip',
    templateUrl: './popular-bets-pod-add-to-betslip.component.html',
    styleUrl: './popular-bets-pod-add-to-betslip.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        SummaryCalculatorService,
        UserService,
        BetslipConnectorService,
        NativePriceFormatterPipe,
        { provide: PickSourceProvider, useClass: ModularPickSourceService },
    ],
})
export class PopularBetsPodAddToBetslipComponent implements OnInit, OnChanges, OnDestroy {
    @Input() enableSinglePickSelection: boolean;
    @Input() podEvents: GridEvent[];
    @Input() tracking: TrackingSource;
    @Input() isEpcotEnabled: boolean;
    @Input() podType: PopularBetsPodTypes;
    @Input() betBuilderMarket?: BetBuilderMarket;
    @Input() betBuilderOptions: IPreCreatedOption[];
    @Input() preCreatedOffer: BetBuilderFixture;

    stake: number;
    winnings?: number;
    totalOdds?: string;
    defaultButtonOverride: boolean = this.popularBetsConfig.defaultButtonOverride;

    private selections: Selection[];
    readonly clickAddToBetslip = new Subject<void>();
    private pickSelectionSubscription: Subscription;
    private isAllPicksAddedToBetslip = false;
    private betBuilderPickIds: BetBuilderPickIdType[] = [];
    settingsInitialized$: Observable<Readonly<IUserBettingSettings>>;
    buttonProperties = signal<ButtonProperties>({
        state: AddToBetslipState.Default,
        text: this.sitecorePopularBetsConfig.messages?.AddToBetslip || '',
    });

    private ngZone = inject(NgZone);

    constructor(
        private summaryCalculatorService: SummaryCalculatorService,
        public sitecorePopularBetsConfig: SitecorePopularBetsConfig,
        private userService: UserService,
        private betslipConnector: BetslipConnectorService,
        private modalDialog: ModalDialogService,
        private pickSource: PickSourceProvider,
        private strategy: RxStrategyProvider,
        private multiReturnsMessageConfig: ReturnsMessageConfig,
        private bettingSettingsService: BettingSettingsService,
        private popularBetsConfig: PopularBetsConfig,
    ) {
        const [addToBetslip$, removeFromBetslip$] = partition(this.clickAddToBetslip, () => !this.isAllPicksAddedToBetslip);

        merge(
            addToBetslip$.pipe(
                map(() => {
                    return this.betslipConnector.canBeAdded(this.podEvents.length);
                }),
                exhaustMap((canBeAdded) => {
                    return canBeAdded ? this.scheduleAddToBetslip$ : this.scheduleOpenDialog$;
                }),
            ),

            removeFromBetslip$.pipe(
                exhaustMap(() => {
                    return this.scheduleRemoveFromBetslip$;
                }),
            ),
        ).subscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.podEvents && !changes.podEvents.firstChange) {
            this.setSelections();
            this.calculateSummary();
            this.updateButtonState();
        }
    }

    ngOnInit(): void {
        this.settingsInitialized$ = this.bettingSettingsService.bettingSettings$.pipe(
            first(),
            tap((bettingSettings) => {
                this.stake = this.getDefaultStake(bettingSettings.defaultStake);
                this.calculateSummary();
                this.updateButtonState();
            }),
        );
        this.setSelections();
        this.subscribeForPickChange();
    }

    get isButtonLocked(): boolean {
        // TODO - below condition will be removed when SGP+ integration with betslip is completed
        if (this.podType === PopularBetsPodTypes.SGPPlus) {
            return true;
        }
        return this.buttonProperties().state === AddToBetslipState.Locked;
    }

    private get isBetBuilderOptions() {
        return this.podType === PopularBetsPodTypes.SGP && this.betBuilderOptions?.filter((o) => o.type === BetBuilderOptionType.Group).length > 0;
    }

    private get isBetBuilderMarkets() {
        return this.podType === PopularBetsPodTypes.SGP && this.preCreatedOffer.precreatedMarketsInfo?.length > 0;
    }

    private get isBetBuilder() {
        return this.podType === PopularBetsPodTypes.SGP;
    }

    private get preCreatedOptionOdds(): NativePrice {
        return NativePrice.fromNativePrice(this.preCreatedOffer.precreatedGroupedInfo[0].builderOptionPricing.odds);
    }

    private isGroupedMarketSuspened(): boolean {
        if (this.isBetBuilder && this.preCreatedOffer.precreatedGroupedInfo) {
            const suspendedOptions = this.podEvents[0].optionGroups.flatMap((og) => og.options?.filter((o) => !o.online));
            const isSuspendedOffer = this.preCreatedOffer.precreatedGroupedInfo[0]?.options?.some((o) =>
                suspendedOptions?.some((vo) => vo.id === o.id),
            );

            const isSuspendedPricing =
                this.preCreatedOffer.precreatedGroupedInfo[0]?.builderOptionPricing.suspensionState === SuspensionState.MarketSuspended;

            return isSuspendedPricing || isSuspendedOffer;
        }

        return false;
    }

    private hasLiveOrLockedRestrictions(): boolean {
        const isAnyEventLive = this.podEvents.some((event) => event.live);
        const optionGroups = this.podEvents.flatMap((event) => event.optionGroups);
        const isAnyMarketLocked = optionGroups.some((og) => !og.online);
        const isAnyOptionLocked = optionGroups.flatMap((og) => og.options).some((o) => !o.online);

        return isAnyEventLive || isAnyMarketLocked || isAnyOptionLocked;
    }

    onButtonClick(): void {
        const { state } = this.buttonProperties();
        if (state !== AddToBetslipState.Locked && state !== AddToBetslipState.Disabled) {
            this.clickAddToBetslip.next();
        }
    }

    private subscribeForPickChange(): void {
        this.betBuilderPickIds = this.getBetBuilderPickIds();

        const pickSelected$ = this.betslipConnector.getPicksSelected(this.isBetBuilder ? [] : this.selections, this.betBuilderPickIds);

        this.pickSelectionSubscription = pickSelected$?.subscribe((pick) => {
            this.isAllPicksAddedToBetslip = pick.every((isPickSelected) => isPickSelected);
            this.updateButtonState();
        });
    }

    private scheduleOpenDialog$ = this.strategy.schedule(
        () => {
            this.modalDialog.openDialog(ClearBetslipComponent, { settings: { cssClass: 'clear-betslip-dialog' } });
        },
        { scope: this, patchZone: this.ngZone },
    );

    private scheduleAddToBetslip$ = this.strategy.schedule(
        () => {
            const tracking = this.getPickTracking();

            if (this.isBetBuilder) {
                this.betslipConnector.addBetBuilderPickToBetslip(
                    this.podEvents[0],
                    this.betBuilderMarket,
                    tracking,
                    this.betBuilderPickIds,
                    this.preCreatedOffer,
                );
            } else {
                this.betslipConnector.addToBetslip(this.selections, this.stake, tracking);
            }
        },
        { scope: this, patchZone: this.ngZone },
    );

    private scheduleRemoveFromBetslip$ = this.strategy.schedule(
        () => {
            if (this.isBetBuilder) {
                const event = this.podEvents[0];

                this.betslipConnector.removeBetBuilderPick(
                    this.preCreatedOffer,
                    this.betBuilderPickIds,
                    event.id,
                    this.betBuilderMarket?.option.longId,
                );

                return;
            }
            this.betslipConnector.removeFromBetslip(this.selections);
        },
        { scope: this, patchZone: this.ngZone },
    );

    private getBetBuilderPickIds(): BetBuilderPickIdType[] {
        if (this.isBetBuilder) {
            const event = this.podEvents[0];
            return this.betslipConnector.getBetBuilderPicks(
                this.preCreatedOffer,
                this.betBuilderMarket,
                event.id,
                event.betBuilderTradingV2FixtureId,
                this.preCreatedOffer?.precreatedGroupedInfo[0],
                event.optionGroups,
            );
        }
        return [];
    }

    private getPickTracking(): IPickTracking {
        const picksTracking = this.pickSource.get('PopularMultiBets');
        picksTracking.betDetails = this.tracking.betDetails;
        picksTracking.variant = this.tracking.variant;
        picksTracking.contentPosition = this.tracking.position?.toString();

        return picksTracking;
    }

    private getDefaultStake(defaultStake?: number): number {
        return defaultStake ? defaultStake : this.multiReturnsMessageConfig.defaultStakes[this.userService.currency.toLowerCase()] || 0;
    }

    private updateButtonState(): void {
        const buttonState = this.getButtonState();
        this.buttonProperties.set({
            state: buttonState,
            text: this.getButtonText(buttonState),
        });
    }

    private getButtonText(state: AddToBetslipState): string {
        if (state === AddToBetslipState.Selected) {
            return this.enableSinglePickSelection
                ? this.sitecorePopularBetsConfig.messages?.AddedAllToBetslip || ''
                : this.sitecorePopularBetsConfig.messages?.AddedToBetslip || '';
        }

        return this.enableSinglePickSelection
            ? this.sitecorePopularBetsConfig.messages?.AddAllToBetslip || ''
            : this.sitecorePopularBetsConfig.messages?.AddToBetslip || '';
    }

    private getButtonState(): AddToBetslipState {
        if (this.isAllPicksAddedToBetslip) {
            return AddToBetslipState.Selected;
        }

        if ((this.podType === PopularBetsPodTypes.Parlay || this.isBetBuilderMarkets) && this.hasLiveOrLockedRestrictions()) {
            return this.isEpcotEnabled ? AddToBetslipState.Locked : AddToBetslipState.Disabled;
        }

        if (this.isBetBuilderOptions && this.isGroupedMarketSuspened()) {
            return this.isEpcotEnabled ? AddToBetslipState.Locked : AddToBetslipState.Disabled;
        }

        return AddToBetslipState.Default;
    }

    private calculateSummary(): void {
        if (this.isBetBuilderOptions) {
            this.totalOdds = this.summaryCalculatorService.getTotalOddsForGroup(this.preCreatedOptionOdds);
            this.winnings = this.summaryCalculatorService.calculateWinningsForGroup(this.stake, this.preCreatedOptionOdds);
        } else {
            this.totalOdds = this.summaryCalculatorService.getTotalOdds(this.selections.map((t) => t.option?.nativePrice).filter(Boolean));
            this.winnings = this.summaryCalculatorService.calculateWinnings(
                this.selections.map((t) => t.option?.nativePrice).filter(Boolean),
                this.stake,
            );
        }
    }

    private setSelections(): void {
        this.selections = this.podEvents.map((event) => {
            const optionGroup = event.optionGroups[0];
            const option = optionGroup.options[0];

            return <Selection>{
                id: `${event.id}-${option.id}`,
                event,
                optionGroup,
                option,
            };
        });
    }

    ngOnDestroy(): void {
        this.pickSelectionSubscription?.unsubscribe();
        this.clickAddToBetslip.complete();
    }
}
